import { canAccept, canDelete, moveNodes } from '@/fs/mutate';
import { displayName } from '@/fs/icons';
import { fsApi } from '@/fs/useFs';
import { useRecycleStore } from '@/recycle/store';
import { useWindowStore } from '@/wm/store';
import { useSettingsStore } from '@/settings/store';
import type { FsNode } from '@/fs/types';
import type { AppArgs } from '@/wm/types';

/**
 * Where a drag can be let go, and what happens when it is.
 *
 * Drop targets announce themselves in the DOM rather than in a registry, so a
 * drag started on the desktop can find one inside an Explorer window it knows
 * nothing about — and a window that closes mid-drag takes its targets with it
 * without having to tell anybody.
 */

/**
 * `folder` is an icon standing for somewhere to put things. `surface` is the
 * background of a view — the desktop itself, or the empty part of a folder
 * window — which takes a drop as a move into the folder it is showing, unless
 * the drag started there, in which case the view rearranges its own icons.
 */
export type DropKind = 'folder' | 'recycle' | 'surface';

export interface DropTarget {
  kind: DropKind;
  /** Destination folder. Empty for the Recycle Bin, which is not a place. */
  path: string;
  /** Which surface declared it: a drop back on your own is a rearrange. */
  owner: string;
  /** The element that declared it, so a drag can light it up. */
  el: HTMLElement;
}

export interface DropPlacement {
  path: string;
  /** Where this item was let go, in client coordinates. */
  x: number;
  y: number;
}

/** Props for an element that accepts drops. Spread onto the container. */
export function dropTargetProps(kind: DropKind, owner: string, path = ''): Record<string, string> {
  return { 'data-drop-kind': kind, 'data-drop-owner': owner, 'data-drop-path': path };
}

/**
 * The folder a node stands for when something is dropped on it. Folders are
 * themselves; a shortcut is whatever it points at, which is how My Documents
 * on the desktop takes a file even though the icon is a `.lnk`.
 */
export function folderBehind(node: FsNode): string | null {
  if (node.type === 'folder') return node.path;
  if (node.data.kind !== 'app') return null;
  if (node.data.appId !== 'explorer') return null;
  const path = node.data.args?.path;
  if (typeof path !== 'string') return null;
  return fsApi.resolve(path)?.type === 'folder' ? path : null;
}

/** The innermost drop target under a point, if there is one. */
export function targetAt(x: number, y: number): DropTarget | null {
  if (typeof document === 'undefined') return null;
  const under = document.elementFromPoint(x, y);
  const el = under instanceof Element ? under.closest<HTMLElement>('[data-drop-kind]') : null;
  if (!el) return null;
  const kind = el.dataset.dropKind;
  if (kind !== 'folder' && kind !== 'recycle' && kind !== 'surface') return null;
  return {
    kind,
    path: el.dataset.dropPath ?? '',
    owner: el.dataset.dropOwner ?? '',
    el,
  };
}

/**
 * Whether letting go here would do anything at all. "Any" rather than "every"
 * on purpose: a mixed selection dropped on a folder moves what it can, the
 * same way a real one did.
 *
 * A view always accepts a drag that started on it, even though the files are
 * already in the folder it is showing: that drop is the surface rearranging
 * itself, which is between the surface and its own icons.
 */
export function isValidDrop(
  target: DropTarget,
  nodes: readonly FsNode[],
  owner: string,
): boolean {
  if (nodes.length === 0) return false;
  if (target.kind === 'surface' && target.owner === owner) return true;
  if (target.kind === 'recycle') return nodes.some(canDelete);
  const dest = fsApi.resolve(target.path);
  return nodes.some((node) => canAccept(dest, node));
}

/* -------------------------------------------------------------------------- */
/* Carrying it out                                                            */
/* -------------------------------------------------------------------------- */

function msgbox(args: AppArgs): void {
  useWindowStore.getState().open('msgbox', args);
}

/**
 * What the confirmation asks, which in XP was four different sentences
 * depending on what was being deleted and where it was going. The bin's
 * Properties sheet decides the "where": with "Do not move files to the Recycle
 * Bin" ticked, nothing is being sent anywhere and the wording says so.
 */
function deleteQuestion(targets: readonly FsNode[], bypass: boolean): string {
  if (targets.length !== 1) {
    return bypass
      ? `Are you sure you want to delete these ${targets.length} items?`
      : `Are you sure you want to send these ${targets.length} items to the Recycle Bin?`;
  }

  const name = displayName(targets[0]);
  if (targets[0].type === 'folder') {
    return bypass
      ? `Are you sure you want to remove the folder '${name}' and all its contents?`
      : `Are you sure you want to remove the folder '${name}' and move all its contents to the Recycle Bin?`;
  }
  return bypass
    ? `Are you sure you want to delete '${name}'?`
    : `Are you sure you want to send '${name}' to the Recycle Bin?`;
}

export interface DeleteOptions {
  /**
   * Shift held: delete outright, without the bin. The one gesture everybody
   * knew, and the same code path as the Properties sheet's own "do not move
   * files to the Recycle Bin".
   */
  permanent?: boolean;
}

/**
 * XP's Confirm File Delete. Asked here as well as from the context menu,
 * because dragging to the bin always raised it too — and skipped entirely when
 * the bin's Properties sheet says not to ask, which is exactly what that
 * checkbox did.
 */
export function confirmDelete(nodes: readonly FsNode[], options: DeleteOptions = {}): void {
  const targets = nodes.filter(canDelete);
  if (targets.length === 0) return;

  const settings = useSettingsStore.getState().recycleBin;
  const bypass = settings.bypass || options.permanent === true;
  const confirm = settings.confirm;
  const { remove, destroy } = useRecycleStore.getState();
  const doIt = () => {
    for (const node of targets) (bypass ? destroy : remove)(node);
  };

  if (!confirm) {
    doIt();
    return;
  }

  const single = targets.length === 1;

  msgbox({
    title: single
      ? targets[0].type === 'folder'
        ? 'Confirm Folder Delete'
        : 'Confirm File Delete'
      : 'Confirm Multiple File Delete',
    icon: 'question',
    text: deleteQuestion(targets, bypass),
    buttons: ['Yes', 'No'],
    onResult: (button: string) => {
      if (button === 'Yes') doIt();
    },
  });
}

/**
 * XP's own wording when a move would land on a name that is already taken. It
 * never offered to merge or rename from a drag — it stopped and said so.
 */
function reportConflicts(nodes: readonly FsNode[]): void {
  const first = nodes[0];
  if (!first) return;
  msgbox({
    title: 'Error Moving File or Folder',
    icon: 'error',
    text:
      nodes.length === 1
        ? `Cannot move ${displayName(first)}: A file with the name you specified already exists. Specify a different file name.`
        : `Cannot move ${nodes.length} items: files with the names you specified already exist. Specify different file names.`,
    buttons: ['OK'],
  });
}

/* -------------------------------------------------------------------------- */
/* Surfaces that lay their own icons out                                      */
/* -------------------------------------------------------------------------- */

export interface Landing {
  node: FsNode;
  /** Where the icon was let go, in client coordinates. */
  x: number;
  y: number;
}

type LandingHandler = (landings: Landing[]) => void;

const surfaces = new Map<string, LandingHandler>();

/**
 * A surface where position means something — the desktop — asks to be told
 * where a drop physically landed, so a file dragged out of a folder window
 * comes to rest under the cursor rather than at the end of the grid.
 *
 * A folder view has nowhere to put that information and simply never
 * registers, which is why this is opt-in rather than part of the drop itself.
 */
export function registerSurface(owner: string, handler: LandingHandler): () => void {
  surfaces.set(owner, handler);
  return () => {
    if (surfaces.get(owner) === handler) surfaces.delete(owner);
  };
}

/**
 * Do the drop. Everything except rearranging a surface's own icons ends up
 * here, whichever window or desktop the drag started on.
 */
export function performDrop(
  target: DropTarget,
  nodes: readonly FsNode[],
  places: readonly DropPlacement[] = [],
): void {
  if (target.kind === 'recycle') {
    confirmDelete(nodes);
    return;
  }

  // Paired up before the move, because moving is exactly what changes the
  // paths these are keyed by.
  const at = new Map<FsNode, DropPlacement>();
  for (const place of places) {
    const node = nodes.find((candidate) => candidate.path === place.path);
    if (node) at.set(node, place);
  }

  const report = moveNodes([...nodes], target.path);
  if (report.conflicts.length > 0) reportConflicts(report.conflicts);
  if (report.moved.length === 0) return;

  const handler = target.kind === 'surface' ? surfaces.get(target.owner) : undefined;
  if (!handler) return;

  const landings: Landing[] = [];
  for (const node of report.moved) {
    const place = at.get(node);
    if (place) landings.push({ node, x: place.x, y: place.y });
  }
  if (landings.length > 0) handler(landings);
}
